import '../../style/Authflow.css';

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FORM_ERROR } from 'final-form';
import {
  GoogleAuthProvider,
  OAuthProvider,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
} from 'firebase/auth';
import PropTypes from 'prop-types';
import { Form } from 'react-final-form';
import { connect } from 'react-redux';

import { CustomerLoginForm } from 'components';
import { AuthorizedRedirect } from 'containers';
import { actions } from 'coupon-common/src/modules/auth/login';
import { api as signupApi } from 'coupon-common/src/modules/signup';
import { dispatchAsAsync } from 'coupon-common/src/utils';
import { Helmet } from 'react-helmet';
import IMG_LOGIN_HERO from '../../assets/images/login_hero.webp';
import { firebaseAuth } from '../../firebase';
import Logo from '../../Logo/Logo';

// Provider instances are created once at module scope, not per-render.
const googleProvider = new GoogleAuthProvider();

const appleProvider = new OAuthProvider('apple.com');
appleProvider.addScope('email');
appleProvider.addScope('name');

const GENERIC_ERROR = 'Something went wrong. Please try again.';
const BUSINESS_ACCOUNT_ERROR =
  'This email belongs to a business account. Please use business login.';
const UNVERIFIED_EMAIL_ERROR =
  'Please verify your email address before signing in.';

// Maps Firebase auth error codes to a single user-facing message.
// Deliberately generic on credential errors so we don't reveal which
// part (email vs password) was wrong.
const getFirebaseErrorMessage = errorCode => {
  switch (errorCode) {
    case 'auth/user-not-found':
    case 'auth/wrong-password':
    case 'auth/invalid-credential':
      return 'No account found with that email and password.';
    case 'auth/too-many-requests':
      return 'Too many attempts. Please try again later.';
    case 'auth/network-request-failed':
      return 'Network error. Please try again.';
    // Social sign-in specific cases. These fire on signInWithPopup,
    // not on the email/password form, but share the same mapper.
    case 'auth/popup-closed-by-user':
    case 'auth/cancelled-popup-request':
      // User backed out of the popup deliberately; no error needed.
      return null;
    case 'auth/account-exists-with-different-credential':
      return 'An account already exists with this email using a different sign-in method.';
    case 'auth/popup-blocked':
      return 'Your browser blocked the sign-in popup. Please allow popups and try again.';
    default:
      return GENERIC_ERROR;
  }
};

// Confirms this Firebase identity has a backend account before logging in —
// mirrors the mobile app's SignInScreen contract. The backend answers HTTP 200
// either way; "account exists" is signaled by `status: 302` in the response
// BODY, and validateFirebaseCredentials() returns that body (response.data).
// Returns one of:
//   'exists'     -> backend customer account exists, safe to log in
//   'signup'     -> no backend account yet (many app accounts were created
//                   with a bug that left them Firebase-only) -> send to signup
//   'business'   -> email belongs to a business account, not allowed here
//   'unverified' -> backend refused to link an unverified email (403)
//   'error'      -> request failed
const checkBackendAccount = async firebaseIdToken => {
  try {
    const data = await signupApi.validateFirebaseCredentials({
      idToken: firebaseIdToken,
    });
    if (data && data.status === 302) {
      return data.role === 'business' ? 'business' : 'exists';
    }
    return 'signup';
  } catch (error) {
    if (error && error.response && error.response.status === 403) {
      return 'unverified';
    }
    console.error('validateFirebaseCredentials error:', error);
    return 'error';
  }
};

// Maps a non-'exists' / non-'signup' check result to a user-facing message.
const getCheckErrorMessage = checkResult => {
  switch (checkResult) {
    case 'business':
      return BUSINESS_ACCOUNT_ERROR;
    case 'unverified':
      return UNVERIFIED_EMAIL_ERROR;
    default:
      return GENERIC_ERROR;
  }
};

export const CustomerLogin = ({ dispatch }) => {

  const navigate = useNavigate();
  const [socialError, setSocialError] = useState(null);
  const [socialLoading, setSocialLoading] = useState(null);

  // No backend account for this Firebase identity yet — send them through
  // signup instead of silently proceeding. displayName is best-effort (Apple
  // often withholds it after the first grant, email/password has none), so
  // the signup form should treat these as optional pre-fills, and
  // firebase_uid/firebaseIdToken are what let signup skip collecting a
  // password (matches the shape mobile's route.params already uses).
  const goToSignup = ({ email, uid, displayName, firebaseIdToken, provider }) => {
    const [firstName = '', ...rest] = (displayName || '').split(' ');
    const lastName = rest.join(' ');

    navigate('/customer-signup', {
      state: {
        userInfo: {
          email,
          firebase_uid: uid,
          firebaseIdToken,
          first_name: firstName,
          last_name: lastName,
        },
        provider,
      },
    });
  };

  const onSubmit = async values => {
    const { email, password } = values;

    let userCredential;
    let firebaseIdToken;
    try {
      userCredential = await signInWithEmailAndPassword(
        firebaseAuth,
        email,
        password,
      );
      firebaseIdToken = await userCredential.user.getIdToken(true);
    } catch (error) {
      console.error('Firebase login error:', error);
      return { [FORM_ERROR]: getFirebaseErrorMessage(error.code) };
    }

    const checkResult = await checkBackendAccount(firebaseIdToken);

    if (checkResult === 'signup') {
      const { uid, displayName } = userCredential.user;
      goToSignup({
        email,
        uid,
        displayName,
        firebaseIdToken,
        provider: 'EMAIL',
      });
      return undefined;
    }

    if (checkResult !== 'exists') {
      // Don't leave a Firebase session behind for an identity we rejected.
      await signOut(firebaseAuth).catch(() => {});
      return { [FORM_ERROR]: getCheckErrorMessage(checkResult) };
    }

    // Same shape as mobile: email + null password + firebaseIdToken.
    // loginSaga stores the Firebase ID token directly as the access token.
    const loginAction = actions.login(email, null, firebaseIdToken);

    return dispatchAsAsync(loginAction, dispatch);
  };

  // Shared handler for Google and Apple. Both resolve to a Firebase
  // user via signInWithPopup, then feed the same login pipeline as
  // the email/password flow (email + null password + firebaseIdToken).
  const handleSocialSignIn = async (provider, providerKey) => {
    setSocialError(null);
    setSocialLoading(providerKey);

    let userCredential;
    try {
      userCredential = await signInWithPopup(firebaseAuth, provider);
    } catch (error) {
      setSocialLoading(null);
      // Ignore user-initiated popup dismissal rather than showing an error.
      if (error.code === 'auth/popup-closed-by-user' || error.code === 'auth/cancelled-popup-request') {
        return;
      }
      console.error('Firebase social login error:', error);
      setSocialError(getFirebaseErrorMessage(error.code));
      return;
    }

    const { email, uid, displayName } = userCredential.user;

    let firebaseIdToken;
    try {
      firebaseIdToken = await userCredential.user.getIdToken(true);
    } catch (error) {
      setSocialLoading(null);
      console.error('Firebase getIdToken error:', error);
      setSocialError(GENERIC_ERROR);
      return;
    }

    const checkResult = await checkBackendAccount(firebaseIdToken);

    if (checkResult === 'signup') {
      setSocialLoading(null);
      goToSignup({
        email,
        uid,
        displayName,
        firebaseIdToken,
        provider: providerKey === 'google' ? 'GOOGLE' : 'APPLE',
      });
      return;
    }

    if (checkResult !== 'exists') {
      setSocialLoading(null);
      // Don't leave a Firebase session behind for an identity we rejected.
      await signOut(firebaseAuth).catch(() => {});
      setSocialError(getCheckErrorMessage(checkResult));
      return;
    }

    const loginAction = actions.login(email, null, firebaseIdToken);

    let result;
    try {
      result = await dispatchAsAsync(loginAction, dispatch);
    } catch (error) {
      console.error('Login dispatch error:', error);
      setSocialError(GENERIC_ERROR);
      setSocialLoading(null);
      return;
    }

    setSocialLoading(null);

    if (result && result[FORM_ERROR]) {
      setSocialError(result[FORM_ERROR]);
    }
  };

  const handleGoogleSignIn = () => handleSocialSignIn(googleProvider, 'google');
  const handleAppleSignIn = () => handleSocialSignIn(appleProvider, 'apple');

  return (
    <div className="auth-page auth-login-page">
      <Helmet>
  <title>Sign In | My Couponbook</title>
  <meta name="description" content="Access your personalized feed of local deals and manage your rewards." />
</Helmet>
      <AuthorizedRedirect isCustomer={true} />
      <img src={IMG_LOGIN_HERO} alt="" className="auth-login-bg" />
      <div className="auth-login-scrim" />

      {/* ── top nav ── */}
      <header className="auth-navbar">
        <button
          type="button"
          className="auth-logo-btn"
          onClick={() => navigate('/')}
          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
        >
          <Logo light />
        </button>
        <button type="button" className="auth-btn auth-btn-solid auth-navbar-cta" onClick={() => navigate('/login')}>
          Click Here for Business Login
        </button>
      </header>

      {/* ── headline ── */}
      <div className="auth-login-marketing">
        <h1 className="auth-login-headline">
          Enjoy Rewards
          <br className="auth-login-break" />
          {' '}for Supporting
          <br className="auth-login-break auth-login-break-second" />
          {' '}<span>Local</span>
        </h1>
      </div>

      {/* ── login card ── */}
      <div className="auth-card-wrap">
        <div className="auth-card">
          <h2 className="auth-card-title">
            Welcome to<br /><span>Couponbook</span>
          </h2>
          <Form
            onSubmit={onSubmit}
            render={formProps => (
              <CustomerLoginForm
                {...formProps}
                onGoogleSignIn={handleGoogleSignIn}
                onAppleSignIn={handleAppleSignIn}
                socialError={socialError}
                socialLoading={socialLoading}
              />
            )}
          />
        </div>
      </div>
    </div>
  );
};

CustomerLogin.propTypes = {
  dispatch: PropTypes.func,
};

export default connect()(CustomerLogin);